// useCommodityTemplates — the DB-backed commodity-flow manifest (mig 876) +
// the per-flow pipeline OVERLAY (mig 879).
//
// commodity_templates is the AUTHORITATIVE source for per-flow behaviour
// (label, flow-specific stages, curated modules, QC-defect commodity, fallback
// grades, capability flags). The in-code FLOW_MANIFEST (flowManifest.ts) stays
// as the offline / loading / fail-safe FALLBACK.
//
// useFlowManifest() merges DB-over-in-code so every consumer keeps a
// synchronous, ALWAYS-DEFINED manifest. CITRUS/BLUEBERRY behave identically to
// before because the mig-876 seed mirrors FLOW_MANIFEST exactly — the DB layer
// only starts to matter for a NEW commodity added as a row (config, not code).
// useFlowPipeline() does the same for the dashboard show/hide+order overlay
// (commodity_template_stages, mig 879) with FLOW_PIPELINE_OVERLAY as fallback.

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useProductionFlow, type ProductionFlow } from '@/lib/useProductionFlow'
import { useActiveTenant } from '@/lib/useActiveTenant'
import { useCommodityGrades } from '@/hooks/useCommodities'
import { FLOW_MANIFEST, FLOW_PIPELINE_OVERLAY, type FlowManifest } from '@/modules/packhouse/flows/flowManifest'
import { resolveFlowPipeline, mergeTenantOverrides, type FlowPipeline, type CommodityTemplateStageRow, type TenantPipelineOverrideRow } from '@/lib/flowPipeline'

const STALE = 5 * 60_000

// Raw commodity_templates row (snake_case, as PostgREST returns it).
interface CommodityTemplateRow {
  flow_key:             string
  label:                string
  flow_specific_stages: string[]
  curated_modules:      string[] | null
  commodity_name:       string | null
  fallback_grades:      string[]
  uses_ppecb:           boolean
  uses_phytclean:       boolean
  uses_force_cooling:   boolean
  has_titration_qc:     boolean
  has_packer_qc:        boolean
  // Optional: not yet a real column (the Phase-C `uses_returnable_bin_loop`
  // capability lives in-code on FLOW_MANIFEST for now). Typed optional so
  // rowToManifest can read it the day the column lands without another change.
  uses_returnable_bin_loop?: boolean
  // Real column since mig 20261020090000. Optional here so a client running
  // ahead of the migration reads undefined and falls back, rather than
  // resolving a berry flow to "weight not tracked" against a DB that still
  // enforces the old CHECK.
  tracks_pallet_weight?: boolean
  sort_order:           number
  is_active:            boolean
}

// A resolved template = the FlowManifest fields + the flow_key it's keyed by.
// Module-private for now — useFlowManifest() is the only public surface. Phase 2
// (the template-admin UI) will export this + useCommodityTemplates when it
// actually consumes them.
interface CommodityTemplate extends FlowManifest {
  flowKey: string
}

function rowToManifest(r: CommodityTemplateRow): CommodityTemplate {
  return {
    flowKey:            r.flow_key,
    label:              r.label,
    flowSpecificStages: r.flow_specific_stages ?? [],
    curatedModules:     r.curated_modules,
    commodityName:      r.commodity_name,
    fallbackGrades:     r.fallback_grades ?? [],
    usesPPECB:          r.uses_ppecb,
    usesPhytclean:      r.uses_phytclean,
    usesForceCooling:   r.uses_force_cooling,
    hasTitrationQc:     r.has_titration_qc,
    hasPackerQc:        r.has_packer_qc,
    // resolveManifest returns the DB row WHOLESALE (not a field merge), so a
    // capability that's in-code-only would resolve to undefined for any flow
    // that has a live commodity_templates row (CITRUS/BLUEBERRY both do). Until
    // it's a real column, fall back to the in-code manifest for this flow; the
    // last-ditch `true` keeps citrus-identical "always render" behaviour for an
    // unknown flow with neither a column nor an in-code entry.
    usesReturnableBinLoop:
      r.uses_returnable_bin_loop
        ?? FLOW_MANIFEST[r.flow_key as ProductionFlow]?.usesReturnableBinLoop
        ?? true,
    // NOT the usual DB-then-in-code chain, and deliberately so. A row exists
    // for this flow but carries no column ⇒ the DB predates mig
    // 20261020090000 ⇒ pallets_shell_or_weighed is still enforcing "weighed or
    // shell". Falling back to the in-code manifest here would read FALSE for
    // berry and let the form offer a weightless save that the DB then rejects
    // (23514) — a broken window for as long as the frontend is deployed ahead
    // of the migration. `true` keeps the client in step with whatever the DB
    // is actually enforcing.
    //
    // The in-code FALSE still applies where it should: with NO template row at
    // all (offline, or a flow the table doesn't know), resolveManifest returns
    // FLOW_MANIFEST[flow] wholesale.
    tracksPalletWeight: r.tracks_pallet_weight ?? true,
  }
}

// All active commodity templates, keyed by flow_key. While loading / offline
// cold the data is undefined → useFlowManifest() falls back to FLOW_MANIFEST,
// so the app never renders without a manifest. Global registry (no tenant
// filter): one cached fetch shared across the app.
function useCommodityTemplates() {
  return useQuery({
    queryKey: ['commodity-templates'],
    staleTime: STALE,
    queryFn: async (): Promise<Record<string, CommodityTemplate>> => {
      const { data, error } = await supabase
        .from('commodity_templates')
        .select('*')
        .eq('is_active', true)
        .order('sort_order')
      if (error) throw error
      const out: Record<string, CommodityTemplate> = {}
      for (const r of (data ?? []) as CommodityTemplateRow[]) {
        out[r.flow_key] = rowToManifest(r)
      }
      return out
    },
  })
}

// Pure precedence: DB template → in-code FLOW_MANIFEST → CITRUS (last-ditch).
// Split out from the hook so the resolution order is one small, obvious unit.
function resolveManifest(
  templates: Record<string, FlowManifest> | undefined,
  flowKey: string,
): FlowManifest {
  return templates?.[flowKey] ?? FLOW_MANIFEST[flowKey as ProductionFlow] ?? FLOW_MANIFEST.CITRUS
}

// The manifest for a flow, DB-over-in-code. `flow` defaults to the active
// packhouse's production flow (useProductionFlow). Always returns a defined manifest.
export function useFlowManifest(flow?: string): FlowManifest {
  const activeFlow = useProductionFlow()
  const { data: templates } = useCommodityTemplates()
  return resolveManifest(templates, flow ?? activeFlow)
}

// Manifests for SEVERAL flows at once — for callers that must reason about every
// lane a packhouse runs (packhouse_config.enabled_flows, mig 20260715170000),
// where one useFlowManifest() per key would break the rules of hooks. Same
// DB-over-in-code resolution, same always-defined guarantee.
export function useFlowManifests(flowKeys: string[]): FlowManifest[] {
  const { data: templates } = useCommodityTemplates()
  const key = flowKeys.join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the joined
  // string: a fresh array literal each render must not re-resolve.
  return useMemo(() => flowKeys.map(f => resolveManifest(templates, f)), [templates, key])
}

// Every active flow a packhouse COULD run, in the templates' own sort order —
// the vocabulary the lane allow-list editor offers (ProductionLanesSection).
// This is the "Phase 2 exports the templates when something actually consumes
// them" moment from the note above; the mapped shape keeps CommodityTemplate
// module-private.
//
// Returns the underlying query alongside the list: `flows` is empty while the
// registry loads AND when it genuinely has no rows, and an editor that rendered
// those the same way would show "no lanes exist" to someone who is merely
// offline. Callers gate on `query`.
export interface AvailableFlow { flowKey: string; label: string; usesForceCooling: boolean }

export function useAvailableFlows(): {
  flows: AvailableFlow[]
  query: ReturnType<typeof useCommodityTemplates>
} {
  const query = useCommodityTemplates()
  const templates = query.data
  const flows = useMemo(
    () => Object.values(templates ?? {}).map(t => ({
      flowKey: t.flowKey, label: t.label, usesForceCooling: t.usesForceCooling,
    })),
    [templates])
  return { flows, query }
}

// Grade vocabulary scoped to THIS tenant's production flow (mig 832 registry).
// The manifest's commodityName is null for citrus, which disables the query and
// falls back to the flow's own fallbackGrades ('1'/'2'/'P') — byte-identical
// for citrus tenants. Berry tenants get their commodity_grades rows ('Class 1'
// /'Class 2'/…); while those load (or if an admin deletes them) the fallback
// stays in the berry vocabulary. This is the SINGLE source for "the grades a
// pallet on this tenant can carry" — never the cross-commodity union, which
// would leak berry 'Class 1' onto a citrus packhouse (and vice versa).
export function useFlowGradeOptions(): string[] {
  const manifest = useFlowManifest()
  const { data: dbGrades = [] } = useCommodityGrades(manifest.commodityName)
  return dbGrades.length > 0 ? dbGrades.map(g => g.code) : manifest.fallbackGrades
}

// ===========================================================================
// Pipeline overlay (mig 879) — the dashboard show/hide + within-band order.
// Pure resolution lives in src/lib/flowPipeline.ts (unit-tested); this is the
// React Query wiring.
// ===========================================================================

// All pipeline-overlay rows (global registry, tenant-less query key). Mirrors
// useCommodityTemplates — one cached fetch shared across the app.
function useCommodityTemplateStages() {
  return useQuery({
    queryKey: ['commodity-template-stages'],
    staleTime: STALE,
    queryFn: async (): Promise<CommodityTemplateStageRow[]> => {
      const { data, error } = await supabase
        .from('commodity_template_stages')
        .select('flow_key, process_key, is_enabled, sort_order')
      if (error) throw error
      return (data ?? []) as CommodityTemplateStageRow[]
    },
  })
}

// All pipeline-OVERRIDE rows for the ACTIVE tenant (mig 885). Tenant-scoped, so
// the query MUST filter tenant_id explicitly — a platform admin bypasses RLS and
// would otherwise aggregate every tenant's rows. queryKey carries the tenant so
// a tenant switch refetches and the persisted cache never crosses tenants.
function useTenantPipelineOverrides() {
  const { data: tenant } = useActiveTenant()
  const tenantId = tenant?.tenantId ?? null
  return useQuery({
    queryKey: ['tenant-pipeline-overrides', tenantId],
    enabled: tenantId !== null,
    staleTime: STALE,
    queryFn: async (): Promise<TenantPipelineOverrideRow[]> => {
      const { data, error } = await supabase
        .from('tenant_pipeline_overrides')
        .select('process_key, is_enabled, sort_order')
        .eq('tenant_id', tenantId!)
      if (error) throw error
      return (data ?? []) as TenantPipelineOverrideRow[]
    },
  })
}

// The commodity DEFAULT pipeline for a flow (NO tenant override) — DB-over-in-code,
// fail-safe to today's behaviour. Memoised (reference-stable). This is the
// baseline the per-tenant override editor diffs against (mig 885).
export function useFlowPipelineDefault(flow?: string): FlowPipeline {
  const activeFlow = useProductionFlow()
  const flowKey = flow ?? activeFlow
  const { data: rows } = useCommodityTemplateStages()
  return useMemo(
    () => resolveFlowPipeline(rows, flowKey, FLOW_PIPELINE_OVERLAY[flowKey as ProductionFlow] ?? { hidden: [] }),
    [rows, flowKey],
  )
}

// The EFFECTIVE pipeline the dashboard renders: the commodity default with the
// active tenant's overrides merged over it (mig 885). Memoised.
export function useFlowPipeline(flow?: string): FlowPipeline {
  const base = useFlowPipelineDefault(flow)
  const { data: overrides } = useTenantPipelineOverrides()
  return useMemo(() => mergeTenantOverrides(base, overrides), [base, overrides])
}
